/** Adapter between the API expense (raw Prisma) and the UI `Expense`.
 *  The list endpoint returns no client name (clientId is optional and rarely set),
 *  so `clientName` stays undefined. */

import type { Expense, ExpenseCategory } from "@/types";

export interface ApiExpense {
  id: string;
  description: string;
  category: ExpenseCategory;
  clientId?: string | null;
  amount: number | string;
  currency?: "DZD" | "USD" | "EUR";
  date: string;
  recurrent: boolean;
}

export interface ExpenseInput {
  description: string;
  category: ExpenseCategory;
  amount: number;
  recurrent: boolean;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function formatDate(raw: string | null | undefined): string {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
}

export function toExpense(api: ApiExpense): Expense {
  return {
    id: api.id,
    description: api.description,
    category: api.category,
    clientName: undefined,
    amount: toNumber(api.amount),
    currency: api.currency ?? "DZD",
    date: formatDate(api.date),
    recurrent: Boolean(api.recurrent),
  };
}

export function toExpenseWriteBody(input: ExpenseInput): Record<string, unknown> {
  return {
    description: input.description,
    category: input.category,
    amount: input.amount,
    currency: "DZD",
    date: new Date().toISOString(),
    recurrent: input.recurrent,
  };
}

export function toMockExpense(input: ExpenseInput): Expense {
  return {
    id: `e_${crypto.randomUUID().slice(0, 8)}`,
    description: input.description,
    category: input.category,
    amount: input.amount,
    currency: "DZD",
    date: new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "short" }),
    recurrent: input.recurrent,
  };
}
